import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { useEffect } from 'react';
import { SUBMIT_LEAD_GQL } from '../../Helpers/submission';
import { getPreSubmissionComponents } from '../__selectors__/component.selectors';
import SubmissionStatus from '../../Data/SubmissionStatus';
import submitPartialLead from '../../Helpers/submitPartialLead';
import {
  STATEMENT_HASH_OVERRIDES,
  STATEMENT_OVERRIDES,
} from '../../Data/Constants';
import { getComponentsBeforePage } from '../../Helpers/components';
import { GenericAdmiralFields } from '../../Data/Components';
import {
  getStateAwareLegalStatement,
  selectCustomerStateCode,
} from '../LegalStatement/legalStatement.helpers';
import selectIsPolarisFeatureEnabled from '../../Helpers/polaris';

/* Performance Impact:
 * The benefit of this wrapper component is to shield <Form /> and its other child
 * components from unnecessary re-runs whenever this self-managed component
 * re-renders. It is considered self-managed because all of its data dependencies
 * originate from the store and not from a parent component.
 */
const PartialLead = () => {
  const { useCidFromURL, widgetLabel, testMode, iFrameId } = useSelector(
    (store) => store.optionalFormConfig
  );
  const oneTrustLocale = useSelector((store) => store.oneTrustLocale);
  const {
    countryCode,
    phoneNumberCountryCode,
    formId,
    campaignId,
    sourceSite,
    pageUrl,
    referrerUrl,
    supplierId,
    privacyPolicy,
  } = useSelector((store) => store.formConfig);
  const { consentCertification } = useSelector((store) => store.trustedForm);
  const legalStatement = useSelector((store) => store.legalStatement);
  const customerStateCode = useSelector(selectCustomerStateCode);
  const { supplierLegalStatements } = getStateAwareLegalStatement({
    legalStatement,
    customerStateCode,
  });
  const components = useSelector((store) => store.components);
  const {
    firstSubmissionAttemptTimestamp,
    partialSubmissionStatus,
    submissionBlocks,
  } = useSelector((store) => store.submission);
  const { parentPageCookies } = useSelector((store) => store.cookies);
  const { supplierMatchSelectionEnabled } = useSelector(
    (store) => store.supplierMatches
  );
  const {
    submissionPageNumber,
    subcategoryName,
    subcategoryId,
    isDomainInternal,
    featureFlags,
  } = useSelector((store) => store.formConfig);
  const { visitorId } = useSelector((store) => store.userIdentities);
  const { themeName } = useSelector((store) => store.theme);
  const oneToOneSupplierConsent = useSelector((store) =>
    selectIsPolarisFeatureEnabled(store, 'oneToOneSupplierConsent')
  );
  const {
    multipleSupplierConsentedLegalStatementHash,
    singleSupplierConsentedLegalStatementHash,
  } = supplierLegalStatements;
  const isSubmissionBlocked = submissionBlocks.length > 0;
  const [submitLeadMutation] = useMutation(SUBMIT_LEAD_GQL);
  const dispatch = useDispatch();
  const preSubmissionComponents = getPreSubmissionComponents(
    components,
    submissionPageNumber
  );

  const privacyPolicyUrl = privacyPolicy?.link || '';
  const consentStatement = STATEMENT_OVERRIDES.EMAIL_ONLY;
  const partialSubmissionLegalStatementHash =
    STATEMENT_HASH_OVERRIDES.EMAIL_ONLY;

  /**
   * If the state indicates that we should resubmit we should try to do so and if the submission is successful then submitLead will set shouldResubmit to false
   */
  useEffect(() => {
    if (
      partialSubmissionStatus ===
        SubmissionStatus.AWAITING_PARTIAL_SUBMISSION &&
      !isSubmissionBlocked
    ) {
      const emailPage = Object.values(preSubmissionComponents).find(
        (component) =>
          component.component.mappedQuestionId === GenericAdmiralFields.EMAIL
      )?.component.page;

      if (emailPage === undefined) {
        return;
      }

      const componentsBeforeAndIncludingEmailPage = getComponentsBeforePage(
        preSubmissionComponents,
        emailPage
      );

      submitPartialLead({
        campaignId,
        components: componentsBeforeAndIncludingEmailPage,
        consentedLegalStatementHash: partialSubmissionLegalStatementHash,
        countryCode,
        dispatch,
        formId,
        oneTrustLocale,
        pageUrl,
        parentPageCookies,
        phoneNumberCountryCode,
        referrerUrl,
        sourceSite,
        submitLeadMutation,
        testMode,
        useCidFromURL,
        widgetLabel,
        multipleSupplierConsentedLegalStatementHash,
        singleSupplierConsentedLegalStatementHash,
        consentCertification,
        supplierId,
        visitorId,
        consentStatement,
        privacyPolicyUrl,
        subcategoryName,
        firstSubmissionAttemptTimestamp,
        subcategoryId,
        iFrameId,
        partialSubmissionPage: emailPage,
        supplierMatchSelectionEnabled,
        isDomainInternal,
        themeName,
        featureFlags,
        oneToOneSupplierConsent,
      });
    }
  }, [partialSubmissionStatus, isSubmissionBlocked]);

  return null;
};

export default PartialLead;
